import { useRef, useState } from "react";
import { FileCode2, Loader2, Upload, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";

const inputCls = "w-full h-11 px-4 rounded-md bg-[#0B0F17] border border-[#242C3D] focus:border-indigo-500 focus:outline-none text-sm transition-colors";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const Tab = ({ on, onClick, icon: Icon, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex-1 h-10 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-colors ${on ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>
    <Icon className="w-4 h-4" /> {children}
  </button>
);

async function runImport(fd) {
  const { data } = await api.post("/html-imports", fd);
  for (let i = 0; i < 60; i++) {
    await sleep(3000);
    const { data: job } = await api.get(`/html-imports/${data.id}`);
    if (job.status === "ready") return job;
    if (job.status === "failed") throw new Error(job.error || "Import failed");
  }
  throw new Error("Import is taking too long — please try again");
}

export const ImportHtmlDialog = ({ onApply }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [base, setBase] = useState("");
  const [replaceForm, setReplaceForm] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const go = async () => {
    const fd = new FormData();
    if (mode === "file") {
      if (!file) return toast.error("Choose an .html file");
      fd.append("file", file);
      if (base.trim()) fd.append("base_url", base.trim());
    } else {
      if (!url.trim()) return toast.error("Enter a page URL");
      fd.append("url", url.trim());
    }
    setBusy(true);
    try {
      const job = await runImport(fd);
      onApply(job.draft, replaceForm);
      (job.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Imported — ${job.images_copied || 0} image(s) copied. Review the page, then click Save.`);
      setOpen(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" data-testid="import-html-open-button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10 text-sm font-medium transition-colors">
        <FileCode2 className="w-4 h-4" /> Import from HTML
      </button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid="import-html-dialog" className="bg-[#141A24] border-[#242C3D] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Import a landing page from HTML</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              AI reads your page and fills in text, colours, fonts, images, speakers, takeaways and form questions. Nothing is saved until you click Save.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1 p-1 rounded-lg bg-[#0B0F17] border border-[#242C3D]">
            <Tab on={mode === "file"} onClick={() => setMode("file")} icon={Upload} testid="import-html-tab-file">HTML file</Tab>
            <Tab on={mode === "url"} onClick={() => setMode("url")} icon={Link2} testid="import-html-tab-url">Page URL</Tab>
          </div>
          {mode === "file" ? (
            <div className="space-y-3">
              <input ref={fileRef} type="file" accept=".html,.htm,text/html" className="hidden" data-testid="import-html-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button type="button" data-testid="import-html-choose-file" onClick={() => fileRef.current?.click()}
                className="w-full h-20 rounded-md border border-dashed border-[#242C3D] hover:border-indigo-500 text-sm text-slate-400 transition-colors">
                {file ? <span data-testid="import-html-file-name" className="text-white">{file.name}</span> : "Choose an .html file (max 3 MB)"}
              </button>
              <input data-testid="import-html-base-url" className={inputCls} value={base} onChange={(e) => setBase(e.target.value)}
                placeholder="Optional: site address for relative image links, e.g. https://example.com" />
            </div>
          ) : (
            <input data-testid="import-html-url-input" className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webinar-page" />
          )}
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" data-testid="import-html-replace-form" checked={replaceForm} onChange={(e) => setReplaceForm(e.target.checked)} className="accent-indigo-500" />
            Replace form questions and opt-ins with the ones found on the page
          </label>
          <button type="button" data-testid="import-html-submit" disabled={busy} onClick={go}
            className="h-11 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading your page… (up to a minute)</> : "Import into this page"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
